import styled from 'styled-components'
import { Card, Nav } from 'react-bootstrap'

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

export const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
`

export const TabsContainer = styled.div`
  padding: 1.25rem 1.25rem 0;
  flex-shrink: 0;
`

export const CustomNavTabs = styled(Nav)`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 0;

  .nav-link {
    color: rgba(255, 255, 255, 0.7);
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
      border-bottom-color: rgba(255, 255, 255, 0.3);
      background: transparent;
    }

    &.active {
      color: #fff;
      background: transparent;
      border-bottom-color: #553c9a;
    }

    &.disabled {
      color: rgba(255, 255, 255, 0.3);
      cursor: not-allowed;
      pointer-events: none;

      &:hover {
        color: rgba(255, 255, 255, 0.3);
        border-bottom-color: transparent;
      }
    }
  }
`

export const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const MainContent = styled.div<{ $showSidebar?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  gap: 1.25rem;
  transition: all 0.2s ease;
  overflow: visible;
`

// =============================================================================
// STATS & TOOLBAR COMPONENTS
// =============================================================================

export const QuickStatsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`

export const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`

export const QuickStat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);

  .icon {
    width: 20px;
    height: 20px;
    padding: 4px;
    border-radius: 6px;
    background: ${(props) => props.color || '#553c9a'};
    color: white;
  }

  .value {
    font-weight: 600;
    font-size: 1.1rem;
  }

  .label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }
`

export const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 0;
`

export const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 300px;
`

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

export const SearchInput = styled.input`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  max-width: 300px;

  &:focus {
    background: #2a2a2a;
    border-color: #553c9a;
    color: white;
    box-shadow: 0 0 0 0.2rem rgba(85, 60, 154, 0.25);
    outline: none;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`

export const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.5);
    z-index: 2;
  }
`

export const ToolbarButton = styled.button`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: inherit;

  &:hover,
  &:focus {
    background: #3a3a3a;
    border-color: rgba(255, 255, 255, 0.3);
    color: white;
  }

  &.active {
    background: #553c9a;
    border-color: #553c9a;
    color: white;
  }

  &:after {
    content: '';
    border: none;
  }
`

export const CreateButton = styled.button`
  background: linear-gradient(135deg, #553c9a, #6b46c1);
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: 1rem;

  &:hover,
  &:focus {
    background: linear-gradient(135deg, #6b46c1, #7c3aed);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(85, 60, 154, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`

export const FilterBadge = styled.span`
  background: #553c9a;
  color: white;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
  border-radius: 10px;
`

// =============================================================================
// CONTENT COMPONENTS
// =============================================================================

export const ModelsContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
`

export const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;

  h2 {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }

  .results-count {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
  }
`

export const GalleryContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: visible;
  padding-right: 0.5rem;
  padding-top: 20px; /* Space for overhanging star icons */

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`

// =============================================================================
// GALLERY COMPONENTS
// =============================================================================

export const ListView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 16px 20px;
  overflow-x: auto;

  /* Custom scrollbar for horizontal scrolling */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`

export const GridView = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  overflow: visible;
  justify-content: center;
  padding: 0;
`

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
  text-align: center;
`

export const ReadyToTrainMessage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  border: 1px dashed rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  text-align: center;

  .icon {
    color: #60a5fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .title {
    font-size: 1rem;
    font-weight: 600;
    color: #60a5fa;
    margin: 0;
  }

  .subtitle {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
  }
`

// =============================================================================
// MODEL CARD COMPONENTS
// =============================================================================

export const EnhancedModelCard = styled.div<{
  $viewMode?: 'grid' | 'list'
  $variant?: 'best' | 'worst'
  $isSelected?: boolean
}>`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: ${(props) => (props.$viewMode === 'list' ? '1rem 1.5rem' : '1.25rem')};
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: ${(props) => (props.$isSelected || props.$variant ? 'visible' : 'hidden')};
  width: 100%;
  max-width: none;

  ${(props) =>
    props.$viewMode === 'list'
      ? `
    display: flex;
    align-items: center;
    gap: 1.5rem;
    min-height: 80px;
    
    @media (max-width: 1200px) {
      gap: 1rem;
      min-height: 70px;
    }
    
    @media (max-width: 1024px) {
      gap: 0.75rem;
      min-height: 60px;
    }
  `
      : `
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    min-height: 200px;
    height: auto;
  `}

  .model-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    flex-shrink: 0;

    &.classification {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    }

    &.object-detection {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
  }

  .model-info {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .model-name-section {
    width: 280px;
    flex-shrink: 0;

    @media (max-width: 1400px) {
      width: 200px;
    }

    @media (max-width: 1200px) {
      width: 160px;
    }
  }

  .model-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .model-metrics {
    display: flex;
    gap: 0.75rem;
    width: 700px;
    flex-shrink: 0;

    @media (max-width: 1400px) {
      gap: 0.5rem;
      width: 630px;
    }

    @media (max-width: 1200px) {
      gap: 0.4rem;
      width: 560px;
    }

    @media (max-width: 1024px) {
      gap: 0.3rem;
      width: 500px;
    }
  }

  .metric-item {
    text-align: center;
    width: 70px;
    flex-shrink: 0;

    @media (max-width: 1400px) {
      width: 60px;
    }

    @media (max-width: 1200px) {
      width: 52px;
    }

    @media (max-width: 1024px) {
      width: 46px;
    }
  }

  .metric-value {
    font-size: 1rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 20px;

    @media (max-width: 1200px) {
      font-size: 0.9rem;
    }

    @media (max-width: 1024px) {
      font-size: 0.85rem;
      min-height: 18px;
    }
  }

  .metric-label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;

    @media (max-width: 1200px) {
      font-size: 0.7rem;
    }

    @media (max-width: 1024px) {
      font-size: 0.65rem;
    }
  }

  .model-badges {
    display: flex;
    gap: 0.75rem;
    width: 180px;
    flex-shrink: 0;
    justify-content: flex-end;
    align-items: center;
  }

  &:hover {
    ${(props) =>
      !props.$isSelected &&
      `
      border-color: rgba(85, 60, 154, 0.6);
      box-shadow: 
        0 0 0 1px rgba(85, 60, 154, 0.3),
        0 4px 20px rgba(85, 60, 154, 0.15),
        0 8px 32px rgba(0, 0, 0, 0.1);
    `}
    background: rgba(42, 42, 42, 0.9);
    transform: translateY(-2px);
  }

  /* Performance corner badge - only for best model */
  ${(props) =>
    props.$variant === 'best' &&
    `
    &::before {
      content: '🏆';
      position: absolute;
      top: -12px;
      left: -12px;
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      z-index: 3;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
      border: 3px solid #1a1a1a;
    }
  `}

  /* Selected state */
  ${(props) =>
    props.$isSelected &&
    `
    border: 2px solid #667eea !important;
    box-shadow: 
      0 0 0 1px rgba(102, 126, 234, 0.5),
      0 4px 20px rgba(102, 126, 234, 0.25),
      0 0 20px rgba(102, 126, 234, 0.1);
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(42, 42, 42, 1) 100%);
    
    &::after {
      content: '★';
      position: absolute;
      top: -12px;
      right: -12px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      z-index: 10;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      border: 3px solid #1a1a1a;
    }
  `}
`

export const CardHeader = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${(props) => props.$viewMode === 'list' && 'flex: 1; min-width: 0;'}

  .model-name-row {
    width: 100%;

    .model-name {
      font-size: ${(props) => (props.$viewMode === 'list' ? '1rem' : '1.125rem')};
      font-weight: 600;
      color: white;
      margin: 0;
      width: 100%;
      ${(props) =>
        props.$viewMode === 'list' &&
        'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;'}
    }
  }

  .model-details-row {
    display: flex;
    align-items: center;
    ${(props) =>
      props.$viewMode === 'grid'
        ? 'justify-content: space-between;'
        : 'justify-content: flex-start;'}
    gap: 1rem;
    width: 100%;

    .model-type {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.875rem;
      flex-shrink: 0;

      svg {
        width: 14px;
        height: 14px;
      }

      &.classification svg {
        color: #10b981;
      }

      &.object-detection svg {
        color: #667eea;
      }
    }
  }
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
`

export const ModelInfo = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${(props) => props.$viewMode === 'list' && 'flex: 1; min-width: 0;'}

  .model-name {
    font-size: ${(props) => (props.$viewMode === 'list' ? '1rem' : '1.125rem')};
    font-weight: 600;
    color: white;
    margin: 0;
    ${(props) =>
      props.$viewMode === 'list' &&
      'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;'}
  }

  .model-type {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.875rem;

    svg {
      width: 14px;
      height: 14px;
    }

    &.classification svg {
      color: #10b981;
    }

    &.object-detection svg {
      color: #667eea;
    }
  }
`

export const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

export const ModelStatus = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  ${(props) => props.$viewMode === 'list' && 'flex-shrink: 0;'}
`

// =============================================================================
// BADGE & STATUS COMPONENTS
// =============================================================================

export const StatusBadge = styled.div<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;

  ${(props) => {
    switch (props.$status.toLowerCase()) {
      case 'trained':
        return 'background: rgba(39, 103, 73, 0.2); color: #68d391; border: 1px solid rgba(39, 103, 73, 0.3);'
      case 'training':
        return 'background: rgba(85, 60, 154, 0.2); color: #b794f6; border: 1px solid rgba(85, 60, 154, 0.3);'
      case 'creating':
        return 'background: rgba(245, 158, 11, 0.2); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.3);'
      case 'initialised':
      case 'initialized':
        return 'background: linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.25) 100%); color: #60a5fa; border: 1px solid rgba(59, 130, 246, 0.4); box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);'
      case 'deployed':
        return 'background: linear-gradient(135deg, rgba(249, 115, 22, 0.25) 0%, rgba(234, 88, 12, 0.25) 100%); color: #fb923c; border: 1px solid rgba(249, 115, 22, 0.4); box-shadow: 0 2px 8px rgba(249, 115, 22, 0.2);'
      case 'err':
      case 'error':
        return 'background: rgba(155, 44, 44, 0.2); color: #fc8181; border: 1px solid rgba(155, 44, 44, 0.3);'
      default:
        return 'background: rgba(113, 128, 150, 0.2); color: #cbd5e0; border: 1px solid rgba(113, 128, 150, 0.3);'
    }
  }}
`

export const ModelStatusBadge = styled.div<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => {
    switch (props.$status) {
      case 'trained':
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      case 'training':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      case 'error':
      case 'err':
        return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
      default:
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
    }
  }};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
`

export const PerformanceBadge = styled.div<{ $variant: 'best' | 'worst' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;

  ${(props) =>
    props.$variant === 'best'
      ? `
    background: linear-gradient(135deg, rgba(39, 103, 73, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%);
    color: #68d391;
    border: 1px solid rgba(39, 103, 73, 0.4);
    box-shadow: 0 2px 8px rgba(39, 103, 73, 0.2);
  `
      : `
    background: linear-gradient(135deg, rgba(155, 44, 44, 0.3) 0%, rgba(185, 28, 28, 0.3) 100%);
    color: #fca5a5;
    border: 1px solid rgba(155, 44, 44, 0.4);
    box-shadow: 0 2px 8px rgba(155, 44, 44, 0.2);
  `}

  svg {
    width: 10px;
    height: 10px;
  }
`

// =============================================================================
// PERFORMANCE & METRICS COMPONENTS
// =============================================================================

export const PerformanceSection = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  ${(props) =>
    props.$viewMode === 'list'
      ? `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  `
      : `
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  `}
`

export const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;

  .stars-container {
    display: flex;
    align-items: center;
    gap: 0.125rem;
  }

  .stars {
    display: flex;
    gap: 0.125rem;
  }
`

export const ModelMetrics = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  ${(props) =>
    props.$viewMode === 'list'
      ? `
  display: flex;
  align-items: center;
    gap: 1rem;
    flex-shrink: 0;
  `
      : `
    flex: 1;
    display: flex;
    flex-direction: column;
  gap: 0.75rem;
  `}
`

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  flex: 1;
`

export const MetricItem = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  padding: ${(props) => (props.$viewMode === 'list' ? '0.375rem 0.5rem' : '0.5rem')};
  text-align: center;

  .metric-label {
    font-size: ${(props) => (props.$viewMode === 'list' ? '0.6rem' : '0.7rem')};
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  .metric-value {
    font-size: ${(props) => (props.$viewMode === 'list' ? '0.8rem' : '0.875rem')};
    font-weight: 600;
    color: white;
  }
`

export const MetricCard = styled.div`
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.8) 0%, rgba(51, 51, 51, 0.8) 100%);
  padding: 1.25rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-color: rgba(102, 126, 234, 0.3);
  }

  .metric-icon {
    width: 40px;
    height: 40px;
    margin: 0 auto 0.75rem;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
    border: 1px solid rgba(102, 126, 234, 0.2);
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.5rem;
    line-height: 1;
  }

  .metric-label {
    font-size: 0.8rem;
    color: #b0b0b0;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`

export const ListMetrics = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  flex: 1;
  min-width: 0;

  .performance-summary {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  .stars-inline {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    flex-shrink: 0;
  }

  .key-metrics {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .metric {
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.8);
    background: rgba(0, 0, 0, 0.2);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    white-space: nowrap;
  }

  .model-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
    flex-shrink: 0;
  }

  .created-date {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
  }

  span {
    white-space: nowrap;
  }
`

export const PerformanceIndicator = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .rating {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    ${(props) => props.$viewMode === 'list' && 'font-size: 0.875rem;'}
  }

  .stars {
    display: flex;
    gap: 0.125rem;
  }
`

// =============================================================================
// ACTION COMPONENTS (Moved to Sidebar)
// =============================================================================

export const ModelActions = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${(props) => props.$viewMode === 'list' && 'flex-shrink: 0;'}

  button {
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.875rem;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &.deploy-btn {
      background: linear-gradient(135deg, #285e61, #319795);
      color: white;

      &:hover {
        background: linear-gradient(135deg, #319795, #4fd1c7);
      }
    }

    &.delete-btn {
      background: rgba(155, 44, 44, 0.2);
      color: #fc8181;
      border: 1px solid rgba(155, 44, 44, 0.3);

      &:hover {
        background: rgba(155, 44, 44, 0.3);
      }
    }
  }
`

// =============================================================================
// SIDEBAR COMPONENTS
// =============================================================================

export const SidebarBackdrop = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  visibility: ${(props) => (props.$show ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
`

export const ModelDetailSidebar = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 220px;
  right: 20px;
  bottom: 20px;
  width: 600px;
  background: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1001;
  transform: translateX(${(props) => (props.$show ? '0' : '100%')});
  transition: transform 0.3s ease;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
`

export const ModelHeaderCard = styled.div`
  background: #2a2a2a;
  padding: 1.5rem 2rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
  }

  .model-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .model-type {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    font-weight: 500;

    &.classification svg {
      color: #10b981;
    }

    &.object-detection svg {
      color: #667eea;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
  }

  .model-status {
    flex-shrink: 0;
  }

  .close-button {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
    padding: 0.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 36px;
    height: 36px;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
      color: #ffffff;
    }

    svg {
      transition: all 0.2s ease;
    }

    &:hover svg {
      transform: rotate(90deg);
    }
  }
`

export const ActionsSection = styled.div`
  padding: 1.25rem 2rem;
  background: linear-gradient(135deg, #2a2a2a 0%, #333333 100%);
  border-bottom: 1px solid #404040;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  .left-actions {
    display: flex;
    gap: 1rem;
  }

  .right-actions {
    display: flex;
    gap: 1rem;
  }

  .select-button {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    color: white;
    padding: 0.75rem 1.25rem;
    border-radius: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
    font-size: 0.9rem;

    &:hover {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    &.selected {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
      cursor: default;

      &:hover {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        transform: none;
        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
      }

      &:disabled {
        opacity: 0.8;
        cursor: not-allowed;
      }
    }
  }

  .deploy-button {
    background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
    border: none;
    color: white;
    padding: 0.75rem 1.25rem;
    border-radius: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(8, 145, 178, 0.2);
    font-size: 0.9rem;

    &:hover {
      background: linear-gradient(135deg, #0e7490 0%, #155e75 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(8, 145, 178, 0.3);
    }

    &.deployed {
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
      box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2) !important;

      &:hover {
        background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%) !important;
        box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3) !important;
      }
    }

    &.deploying {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2) !important;
      cursor: not-allowed;

      &:hover {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        transform: none;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2) !important;
      }

      /* Ensure deploying styles override disabled styles */
      &:disabled {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2) !important;
        cursor: not-allowed;

        &:hover {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
          transform: none;
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2) !important;
        }
      }
    }

    &:disabled {
      background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
      cursor: not-allowed;
      box-shadow: none;

      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  }
`

export const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  background: linear-gradient(180deg, #1a1a1a 0%, #1e1e1e 100%);

  .content-inner {
    padding: 2rem;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`

export const DetailSection = styled.div`
  margin-bottom: 1.5rem;
  background: rgba(42, 42, 42, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    svg {
      color: #667eea;
    }
  }

  .section-content {
    padding: 1.5rem;
  }
`

export const DangerZone = styled.div`
  margin-top: auto;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, rgba(155, 44, 44, 0.05) 0%, rgba(185, 28, 28, 0.05) 100%);
  border-top: 1px solid rgba(220, 38, 38, 0.2);
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);

  .danger-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: #fca5a5;
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .danger-description {
    color: rgba(252, 165, 165, 0.8);
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
    line-height: 1.4;
  }

  .delete-button {
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%);
    border: 1px solid rgba(220, 38, 38, 0.3);
    color: #fca5a5;
    padding: 0.75rem 1.25rem;
    border-radius: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.9rem;
    width: 100%;
    justify-content: center;

    &:hover {
      background: linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(185, 28, 28, 0.2) 100%);
      border-color: rgba(220, 38, 38, 0.5);
      color: #ffffff;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
    }
  }
`

// =============================================================================
// DATASET & CLASS COMPONENTS
// =============================================================================

export const DatasetCard = styled.div<{ $type: 'training' | 'validation' }>`
  background: ${(props) =>
    props.$type === 'training'
      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.12) 100%)'
      : 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.12) 100%)'};
  border: 1px solid
    ${(props) =>
      props.$type === 'training' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(16, 185, 129, 0.3)'};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

  .dataset-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;

    .dataset-indicator {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${(props) =>
        props.$type === 'training'
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
      box-shadow: 0 2px 8px
        ${(props) =>
          props.$type === 'training' ? 'rgba(102, 126, 234, 0.4)' : 'rgba(16, 185, 129, 0.4)'};
      border: 2px solid rgba(255, 255, 255, 0.1);
    }

    .dataset-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #ffffff;
      text-transform: capitalize;
    }
  }

  .dataset-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1.25rem;
  }

  .stat-item {
    text-align: center;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);

    .stat-value {
      font-size: 1.75rem;
      font-weight: 700;
      color: ${(props) => (props.$type === 'training' ? '#667eea' : '#10b981')};
      margin-bottom: 0.5rem;
      line-height: 1;
    }

    .stat-label {
      font-size: 0.85rem;
      color: #b0b0b0;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`

export const ClassDisplayCard = styled.div`
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-top: 0.5rem;
  backdrop-filter: blur(10px);

  .classes-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.25rem;

    .classes-title {
      font-size: 1rem;
      font-weight: 600;
      color: #ffffff;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      svg {
        color: #667eea;
      }
    }

    .classes-count {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 0.375rem 0.875rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  .classes-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    max-height: 220px;
    overflow-y: auto;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(102, 126, 234, 0.5);
      border-radius: 2px;

      &:hover {
        background: rgba(102, 126, 234, 0.7);
      }
    }
  }

  .class-tag {
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.15) 0%,
      rgba(118, 75, 162, 0.15) 100%
    );
    color: #ffffff;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
    border: 1px solid rgba(102, 126, 234, 0.2);
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(102, 126, 234, 0.25) 0%,
        rgba(118, 75, 162, 0.25) 100%
      );
      border-color: rgba(102, 126, 234, 0.4);
      transform: translateY(-1px);
    }
  }
`

export const OverviewCard = styled.div`
  background: linear-gradient(135deg, rgba(42, 42, 42, 0.6) 0%, rgba(51, 51, 51, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

  .overview-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .overview-label {
      color: #b0b0b0;
      font-size: 0.9rem;
      font-weight: 500;
    }

    .overview-value {
      color: #ffffff;
      font-weight: 600;
      font-size: 0.95rem;
    }
  }
`

// =============================================================================
// NOTIFICATION & CARD COMPONENTS
// =============================================================================

export const NotificationCard = styled(Card)`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: white;
  margin: 2rem auto;
  max-width: 500px;

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #553c9a;
    margin: 0 auto 1.5rem;
  }

  h4 {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1.5rem;
  }

  button {
    background: #553c9a;
    border: none;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 auto;

    &:hover,
    &:active {
      background: #6b4cc0;
    }
  }
`

export const StyledCard = styled(Card)<{ $small?: boolean }>`
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: ${(props) => (props.$small ? '0.75rem' : '1.25rem')};
  height: 100%;
  color: white;
  display: flex;
  flex-direction: column;

  .card-title {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1rem;
    font-weight: 500;
  }
`

export const StyledPerformanceCard = styled(StyledCard)<{
  $variant?: 'best' | 'worst'
  $small?: boolean
  $isClassification?: boolean
}>`
  .performance-grid {
    display: grid;
    grid-template-columns: ${(props) => {
      if (props.$small) return 'repeat(4, 1fr)'
      return props.$isClassification ? '1fr' : 'repeat(2, 1fr)'
    }};
    gap: ${(props) => (props.$small ? '0.5rem' : '0.75rem')};
    margin-top: auto;
    flex: 1;
    align-content: center;
  }

  .metric-item {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    text-align: center;

    ${(props) =>
      props.$isClassification
        ? `
      aspect-ratio: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 1rem;
    `
        : `
      padding: 0.75rem 0.5rem;
    `}

    .metric-label {
      font-size: ${(props) => (props.$small ? '0.65rem' : '0.75rem')};
      color: rgba(255, 255, 255, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.25rem;
    }

    .metric-value {
      font-size: ${(props) => (props.$small ? '0.8rem' : '1rem')};
      font-weight: 600;
      color: ${(props) => {
        switch (props.$variant) {
          case 'best':
            return '#68d391'
          case 'worst':
            return '#fc8181'
          default:
            return 'white'
        }
      }};
    }
  }

  ${(props) =>
    props.$variant &&
    `
    border-color: ${props.$variant === 'best' ? '#276749' : '#9b2c2c'};
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: ${props.$variant === 'best' ? '#276749' : '#9b2c2c'};
    }
  `}
`
